import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";

// Lazy-load non-critical routes to keep the initial JS bundle small (improves FCP/LCP).
const Platform = lazy(() => import("./pages/Platform.tsx"));
const AIGovernance = lazy(() => import("./pages/AIGovernance.tsx"));
const Modules = lazy(() => import("./pages/Modules.tsx"));
const ConsentManagement = lazy(() => import("./pages/ConsentManagement.tsx"));
const DuaaComplaints = lazy(() => import("./pages/DuaaComplaints.tsx"));
const IndiaDpdp = lazy(() => import("./pages/IndiaDpdp.tsx"));
const DocumentRedaction = lazy(() => import("./pages/DocumentRedaction.tsx"));
const ContractReview = lazy(() => import("./pages/ContractReview.tsx"));
const ModuleDetailPage = lazy(() => import("./components/ModuleDetailPage.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const BookDemo = lazy(() => import("./pages/BookDemo.tsx"));
const OrganisationApply = lazy(() => import("./pages/OrganisationApply.tsx"));
const StartTrial = lazy(() => import("./pages/StartTrial.tsx"));
const SwitchOffer = lazy(() => import("./pages/Switch.tsx"));
const Toolkits = lazy(() => import("./pages/Toolkits.tsx"));
const ToolkitDetail = lazy(() => import("./pages/ToolkitDetail.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Partners = lazy(() => import("./pages/Partners.tsx"));
const PartnersMsp = lazy(() => import("./pages/PartnersMsp.tsx"));
const PartnersLawFirms = lazy(() => import("./pages/PartnersLawFirms.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions.tsx"));
const AITermsAddendum = lazy(() => import("./pages/AITermsAddendum.tsx"));
const DataResidency = lazy(() => import("./pages/DataResidency.tsx"));
const AdminConsents = lazy(() => import("./pages/AdminConsents.tsx"));
const DsarCarousel = lazy(() => import("./pages/DsarCarousel.tsx"));
const CompareIndex = lazy(() => import("./pages/compare/CompareIndex.tsx"));
const PrivacyVsOneTrust = lazy(() => import("./pages/compare/PrivacyVsOneTrust.tsx"));
const PrivacyVsTrustArc = lazy(() => import("./pages/compare/PrivacyVsTrustArc.tsx"));
const PrivacyVsOneTrustVsTrustArc = lazy(() => import("./pages/compare/PrivacyVsOneTrustVsTrustArc.tsx"));
const PrivacyVsPrivIQ = lazy(() => import("./pages/compare/PrivacyVsPrivIQ.tsx"));
const PrivacyByFormiti = lazy(() => import("./pages/compare/PrivacyByFormiti.tsx"));
const OneTrustLmsVsPrivacy360Lms = lazy(() => import("./pages/compare/OneTrustLmsVsPrivacy360Lms.tsx"));
const PartnerProgramsVsPrivIQ = lazy(() => import("./pages/compare/PartnerProgramsVsPrivIQ.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

export const AppRoutes = () => (
  <Suspense fallback={null}>
    <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/ai-governance" element={<AIGovernance />} />
            <Route path="/modules" element={<Modules />} />
            <Route path="/consent-management" element={<ConsentManagement />} />
            <Route path="/duaa-complaints" element={<DuaaComplaints />} />
            <Route path="/india-dpdp-compliance" element={<IndiaDpdp />} />
            <Route path="/modules/contract-review" element={<ContractReview />} />
            <Route path="/modules/document-redaction" element={<DocumentRedaction />} />
            <Route path="/modules/:slug" element={<ModuleDetailPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/book-demo" element={<BookDemo />} />
            <Route path="/organisation/apply" element={<OrganisationApply />} />
            <Route path="/start-trial" element={<StartTrial />} />
            <Route path="/switch" element={<SwitchOffer />} />
            <Route path="/toolkits" element={<Toolkits />} />
            <Route path="/toolkits/:slug" element={<ToolkitDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/partners/msp" element={<PartnersMsp />} />
            <Route path="/partners/law-firms" element={<PartnersLawFirms />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/ai-terms-addendum" element={<AITermsAddendum />} />
            <Route path="/data-residency" element={<DataResidency />} />
            <Route path="/admin/consents" element={<AdminConsents />} />
            <Route path="/internal/carousel/dsar-redaction" element={<DsarCarousel />} />
            <Route path="/compare" element={<CompareIndex />} />
            <Route path="/compare/privacy360-vs-onetrust" element={<PrivacyVsOneTrust />} />
            <Route path="/compare/privacy360-vs-trustarc" element={<PrivacyVsTrustArc />} />
            <Route path="/compare/privacy360-vs-onetrust-vs-trustarc" element={<PrivacyVsOneTrustVsTrustArc />} />
            <Route path="/compare/privacy360-vs-priviq" element={<PrivacyVsPrivIQ />} />
            <Route path="/compare/privacy360-by-formiti" element={<PrivacyByFormiti />} />
            <Route path="/compare/onetrust-lms-vs-privacy360-lms" element={<OneTrustLmsVsPrivacy360Lms />} />
            <Route path="/compare/partner-programs-privacy360-vs-priviq" element={<PartnerProgramsVsPrivIQ />} />
            <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
